import { useMemo, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import type { SupplierRow } from '../../data/catalog.ts'
import { sortSuppliers, supplierChanges, supplierDefinePayload, supplierUpdatePayload } from '../../data/catalogueUi.ts'
import { Field, Pill } from '../../ui/components.tsx'
import { useWorkshopAction } from '../../ui/hooks.ts'
import { Feedback, useCatalogueAccess, type Access } from './shared.tsx'

/** Suppliers: who the club buys from. Nothing about money lives here. A supplier is never deleted; an old one is archived and stays on the products and batches that name it. */
export function SuppliersTab({ suppliers }: { suppliers: readonly SupplierRow[] }) {
  const { t } = useTranslation()
  const access = useCatalogueAccess()
  const rows = useMemo(() => sortSuppliers(suppliers), [suppliers])
  return (
    <section className="flex flex-col gap-4" data-testid="suppliers-panel">
      {access.allowed && <AddSupplier access={access} />}
      {rows.length === 0 && <p className="opacity-70" data-testid="supplier-none">{t('catalogue.suppliers.none')}</p>}
      <ul className="flex flex-col gap-3" data-testid="supplier-list">
        {rows.map((s) => <SupplierCard key={s.id} supplier={s} access={access} />)}
      </ul>
    </section>
  )
}

function AddSupplier({ access }: { access: Access }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const id = useRef(newId())
  const [name, setName] = useState('')
  const [contact, setContact] = useState('')
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !access.canSave) return
    void w.run('supplier.define@1', supplierDefinePayload(id.current, name, contact), 'catalogue.suppliers.added', { aggregateId: id.current })
      .then((ok) => { if (ok) { id.current = newId(); setName(''); setContact('') } })
  }
  return (
    <form onSubmit={submit} noValidate className="card flex flex-col gap-3" data-testid="supplier-form">
      <h2 className="text-lg font-bold">{t('catalogue.suppliers.new')}</h2>
      <Field label={t('catalogue.suppliers.name')} htmlFor="sup-name"><input id="sup-name" className="input" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} data-testid="supplier-name" /></Field>
      <Field label={t('catalogue.suppliers.contact')} hint={t('catalogue.suppliers.contactHint')} htmlFor="sup-contact">
        <input id="sup-contact" className="input" maxLength={300} value={contact} onChange={(e) => setContact(e.target.value)} data-testid="supplier-contact" />
      </Field>
      <Feedback id="supplier-add" error={w.error} ok={w.ok} pending={w.pending} />
      <button className="btn btn-primary" disabled={w.busy || !name.trim() || !access.canSave} data-testid="supplier-add">{w.busy ? t('common.saving') : t('catalogue.suppliers.add')}</button>
    </form>
  )
}

function SupplierCard({ supplier: s, access }: { supplier: SupplierRow; access: Access }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const [editing, setEditing] = useState(false)
  return (
    <li className="card flex flex-col gap-2" data-testid="supplier-row" data-supplier={s.id}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="min-w-0 break-words text-lg font-bold" data-testid="supplier-name-text">{s.name}</p>
        {s.archived_at && <span data-testid="supplier-archived-pill"><Pill tone="amber">{t('catalogue.archived')}</Pill></span>}
      </div>
      {s.contact && <p className="meta break-words" data-testid="supplier-contact-text">{s.contact}</p>}
      <Feedback id="supplier-row" error={editing ? null : w.error} ok={w.ok} pending={w.pending} />
      {access.allowed && !editing && <button type="button" className="btn !min-h-11 self-start" onClick={() => { w.reset(); setEditing(true) }} data-testid="supplier-edit">{t('common.edit')}</button>}
      {editing && <EditSupplier supplier={s} w={w} access={access} onDone={() => setEditing(false)} />}
    </li>
  )
}

function EditSupplier({ supplier: s, w, access, onDone }: { supplier: SupplierRow; w: ReturnType<typeof useWorkshopAction>; access: Access; onDone: () => void }) {
  const { t } = useTranslation()
  const [name, setName] = useState(s.name)
  const [contact, setContact] = useState(s.contact ?? '')
  const set = supplierChanges(s, name, contact)
  const dirty = Object.keys(set).length > 0
  const archived = !!s.archived_at
  const save = (e: FormEvent) => {
    e.preventDefault()
    if (!dirty || !name.trim() || !access.canSave) return
    void w.run('supplier.update@1', supplierUpdatePayload(s, set), 'catalogue.suppliers.saved', { aggregateId: s.id }).then((ok) => { if (ok) onDone() })
  }
  return (
    <form onSubmit={save} noValidate className="flex flex-col gap-3" data-testid="supplier-edit-form">
      <Field label={t('catalogue.suppliers.name')} htmlFor={`se-name-${s.id}`}><input id={`se-name-${s.id}`} className="input" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} data-testid="supplier-edit-name" /></Field>
      <Field label={t('catalogue.suppliers.contact')} hint={t('catalogue.suppliers.contactHint')} htmlFor={`se-contact-${s.id}`}>
        <input id={`se-contact-${s.id}`} className="input" maxLength={300} value={contact} onChange={(e) => setContact(e.target.value)} data-testid="supplier-edit-contact" />
      </Field>
      <Feedback id="supplier-edit" error={w.error} pending={w.pending} />
      <div className="flex flex-wrap gap-2">
        <button className="btn btn-primary !min-h-11 flex-1" disabled={w.busy || !dirty || !name.trim() || !access.canSave} data-testid="supplier-save">{w.busy ? t('common.saving') : t('common.save')}</button>
        <button type="button" className="btn !min-h-11" onClick={onDone} data-testid="supplier-edit-cancel">{t('common.cancel')}</button>
      </div>
      <div className="flex flex-col gap-2 border-t-2 border-slate-200 pt-3 dark:border-slate-700">
        <p className="meta">{archived ? t('catalogue.suppliers.archivedHelp') : t('catalogue.suppliers.archiveHelp')}</p>
        <button type="button" className="btn !min-h-11" disabled={w.busy || !access.canSave} data-testid="supplier-archive"
          onClick={() => void w.run('supplier.update@1', supplierUpdatePayload(s, { archived: !archived }), archived ? 'catalogue.suppliers.unarchivedDone' : 'catalogue.suppliers.archivedDone', { aggregateId: s.id }).then((ok) => { if (ok) onDone() })}>
          {archived ? t('catalogue.unarchive') : t('catalogue.archive')}
        </button>
      </div>
    </form>
  )
}
